import React, { useMemo, useRef, useState } from 'react';
import {
  AsyncConnector,
  DataGrid,
  DataGridHeader,
  Layout,
  LayoutItem,
  Suspense,
  useApi,
  Button,
  Flashy,
  PluralizeUtil,
} from '@axiom/ui';
import type { Opportunity, UserInterview } from '@axiom/validation';
import moment from 'moment-timezone';

import { useInterval } from '../../hooks/useInterval';
import { InterviewGridCellsUtil } from '../../utils/InterviewGridCellsUtil';
import {
  InterviewGridCancelModal,
  type CancelEventSubmitProps,
} from '../InterviewGridCancelModal/InterviewGridCancelModal';
import { useInterviewAvailabilityCandidateData } from '../../hooks/useInterviewAvailabilityCandidateData';
import {
  CalendarScheduleModes,
  TalentInterviewAvailabilityModal,
} from '../TalentInterviewAvailabilityModal/TalentInterviewAvailabilityModal';
import { UserApi } from '../../api/user-api';
import { CalendarApi } from '../../api/calendar-api';
import { OpportunityApi } from '../../api/opportunity-api';
import { CalendarConversationsApi } from '../../api/calendar-conversations-api';
import { InterviewAttendeesAndLinkModalEdit } from '../InterviewAttendeesAndLinkModalEdit/InterviewAttendeesAndLinkModalEdit';

export const OpportunityInterviews = ({
  opportunity,
}: {
  opportunity: Opportunity;
}) => {
  const [{ data: user }] = useApi(UserApi.readSessionUser());
  const [resultCount, setResultCount] = useState(0);
  const [activeInterviewData, setActiveInterviewData] =
    useState<UserInterview | null>(null);
  const [deleteEvent, setDeleteEvent] = useState<UserInterview | null>(null);
  const [editAttendeesData, setEditAttendeesData] =
    useState<UserInterview | null>(null);
  const [editInterviewTimeData, setEditInterviewTimeData] =
    useState<UserInterview | null>(null);
  const { refreshInterval, stateChangeTrigger } = useInterval(5 * 60 * 1000); // 5min

  const activeCandidateSource = activeInterviewData ?? editInterviewTimeData;
  const { interviewCandidate } = useInterviewAvailabilityCandidateData({
    candidateInterview: activeCandidateSource?.candidateOpportunity.candidate,
  });

  const GridConnector = useMemo(() => {
    return new AsyncConnector({
      pageSize: 20,
      getData: ({ page }) => {
        return OpportunityApi.readOpportunityInterviews({
          opportunityId: opportunity.id,
          body: {
            timezone: moment.tz.guess(),
            page: page || 1,
            pageSize: 20,
            sort: 'InterviewStatus',
            order: 'asc',
          },
        });
      },
      parseData: ({ meta, data }) => {
        setResultCount(meta.resultCount);
        return {
          currentPage: meta.currentPage,
          totalPages: meta.pageCount,
          data,
        };
      },
    });
  }, [opportunity, stateChangeTrigger]);

  const runDelay = async () => {
    // add delay before refresh
    await new Promise(resolve => {
      setTimeout(() => {
        resolve(true);
      }, 1.5 * 1000); // 1.5 seconds
    });
  };

  const handleOpenInterviewModal = (data: UserInterview) => {
    setActiveInterviewData(data);
  };
  const handleCloseInterviewModal = async (addDelay = false) => {
    setActiveInterviewData(null);

    /**
     * Due to the submitting of the calendar data is within the modal
     * we only have access to the close function. So we delay here
     * before refreshing the data on the grid
     * */
    if (addDelay) {
      await runDelay();
    }

    refreshInterval();
  };

  const handleOpenInterviewGridCancelModal = (data: UserInterview) => {
    setDeleteEvent(data);
  };

  const handleDeleteSubmit = async (data: CancelEventSubmitProps) => {
    await CalendarApi.deleteCalendarEvents(data.calendarId, [data.eventId]);

    await runDelay();

    if (data.calendarId) {
      await Promise.all([
        CalendarApi.refreshCalendar(data.calendarId),
        CalendarApi.refreshCalendarEvents(data.calendarId),
      ]);
    }

    refreshInterval();
    setDeleteEvent(null);
  };

  // ag-grid context menu items can fire their action twice per click (the
  // hook attaches its own click handler to the same element our inline
  // onClick is on, so stopPropagation doesn't help). Guard the dismiss
  // handler with an in-flight ref so a single user click only produces one
  // DELETE request.
  const dismissInFlightRef = useRef(false);
  const handleDismissRequest = async (interview: UserInterview) => {
    if (dismissInFlightRef.current) return;
    if (!interview.clientRequest?.id) return;
    dismissInFlightRef.current = true;
    try {
      await CalendarConversationsApi.deleteInterviewRequest(
        interview.clientRequest.id
      );
      await runDelay();
      refreshInterval();
    } finally {
      dismissInFlightRef.current = false;
    }
  };

  return (
    <>
      <Layout name="OPPORTUNITY_INTERVIEWS" direction="vertical" stretched>
        <LayoutItem position="right" verticalGutter="16px">
          <Button
            name="refresh_button"
            pattern="primary"
            variation="outline"
            onClick={() => {
              refreshInterval();
            }}
          >
            Refresh
          </Button>
        </LayoutItem>
        <LayoutItem name="RESULT_COUNT_DISPLAY" bottomGutter="8px">
          <Flashy bold>
            {resultCount}{' '}
            {PluralizeUtil({ text: 'Interview', count: resultCount })}
          </Flashy>
        </LayoutItem>
        <LayoutItem stretched>
          <DataGrid
            name="INTERVIEWS_GRID"
            displayMode="infinite"
            dataSource={GridConnector}
            noResultsMessage="Interviews that are assigned to this opportunity will appear here"
            getContextMenuItems={menuData => {
              return InterviewGridCellsUtil.getContextMenuItems({
                params: menuData,
                openScheduleInterviewModal: handleOpenInterviewModal,
                openEditAttendeesModal: setEditAttendeesData,
                openEditInterviewTimeModal: setEditInterviewTimeData,
                openInterviewGridCancelModal:
                  handleOpenInterviewGridCancelModal,
                onDismissRequest: (interview: UserInterview) => {
                  void handleDismissRequest(interview);
                },
              });
            }}
            allowContextMenu
            simple
          >
            <DataGridHeader<UserInterview>
              name="status"
              displayName="Status"
              width={152}
              cellRender={InterviewGridCellsUtil.status}
            />
            <DataGridHeader<UserInterview>
              name="talent"
              displayName="Talent"
              width={365}
              cellRender={InterviewGridCellsUtil.talent}
            />
            <DataGridHeader<UserInterview>
              name="date_and_round"
              displayName="Interview date & round"
              width={365}
              cellRender={props =>
                InterviewGridCellsUtil.dateAndRound(props, {
                  onClick: handleOpenInterviewModal,
                })
              }
            />
            <DataGridHeader<UserInterview>
              name="attendees"
              displayName="Confirmed attendees"
              width={397}
              cellRender={InterviewGridCellsUtil.attendees}
            />
            <DataGridHeader<UserInterview>
              name="meeting_link"
              displayName="Meeting link"
              width={160}
              cellRender={props =>
                InterviewGridCellsUtil.meetingLink(props, {
                  onAddLink: setEditAttendeesData,
                })
              }
            />
            <DataGridHeader<UserInterview>
              name="contextMenu"
              pinned="right"
              lockPinned
              displayName=""
              width={64}
              cellRender={InterviewGridCellsUtil.contextMenu}
            />
          </DataGrid>
        </LayoutItem>
      </Layout>
      {activeInterviewData && interviewCandidate ? (
        <Suspense>
          <TalentInterviewAvailabilityModal
            name="INTERVIEW_AUTO_MODAL"
            user={user}
            candidate={interviewCandidate}
            onClose={addDelay => void handleCloseInterviewModal(addDelay)}
            clientRequestId={activeInterviewData.clientRequest?.id}
            opportunityId={activeInterviewData.opportunityId}
            opportunityCandidateStatus={
              activeInterviewData.candidateOpportunity.candidateStatus
            }
            defaultMode={CalendarScheduleModes.INTERVIEW}
          />
        </Suspense>
      ) : null}
      {deleteEvent ? (
        <Suspense>
          <InterviewGridCancelModal
            name="DELETE_INTERVIEW_MODAL"
            data={deleteEvent}
            onClose={() => {
              setDeleteEvent(null);
            }}
            onSchedule={handleOpenInterviewModal}
            onSubmit={async data => {
              await handleDeleteSubmit(data);
            }}
          />
        </Suspense>
      ) : null}
      {editAttendeesData ? (
        <Suspense>
          <InterviewAttendeesAndLinkModalEdit
            interview={editAttendeesData}
            onClose={() => {
              setEditAttendeesData(null);
            }}
            onSave={async () => {
              await runDelay();
              refreshInterval();
            }}
          />
        </Suspense>
      ) : null}
      {editInterviewTimeData && interviewCandidate ? (
        <Suspense>
          <TalentInterviewAvailabilityModal
            name="EDIT_INTERVIEW_TIME_MODAL"
            user={user}
            candidate={interviewCandidate}
            editableEventId={
              editInterviewTimeData.calendarConversation?.invitees.find(
                i => i.candidate
              )?.id ?? null
            }
            editableConversationId={
              editInterviewTimeData.calendarConversation?.id ?? null
            }
            onClose={addDelay => {
              void handleCloseInterviewModal(addDelay);
              setEditInterviewTimeData(null);
            }}
            opportunityId={editInterviewTimeData.opportunityId}
            opportunityCandidateStatus={
              editInterviewTimeData.candidateOpportunity.candidateStatus
            }
            defaultMode={CalendarScheduleModes.INTERVIEW}
          />
        </Suspense>
      ) : null}
    </>
  );
};
